import { DEFAULT_DISPLAY, type BoardDisplay, type ListDisplay, type OverviewCard, type ProjectDisplayDto, type ProjectDto, type ProjectHome, type ProjectSnapshotDto, type TaskDto } from '@werft/contracts';
import type { ProjectHealth, ProjectPhase, ProjectTool } from '@werft/domain';
import { t } from '../../i18n/index.js';
import { ApiError, newOpId } from '../../platform/api.js';
import { keys, queryClient } from '../../platform/queries.js';
import { upsertTask } from '../../platform/realtime.js';
import { outbox } from '../../platform/sync.js';
import { setPendingSource, withPendingProject } from '../../platform/projectPending.js';
import { toast } from '../../platform/toast.js';
import { pinnedFeedback } from './pins.js';

/**
 * Projekt-Insel (Paket P9): Werkzeuge zuschalten/ausblenden, Startseite, Projektstatus, Anheften. Über den
 * Ausgangskorb; die Wirkung erscheint sofort im Projektstand.
 */

const failed = (err: unknown): void => void toast(t('sync.failed', { message: err instanceof Error ? err.message : t('task.failed') }), { kind: 'error' });

function applyProject(pid: string, p: Partial<ProjectDto>) {
  queryClient.setQueryData<ProjectSnapshotDto>(keys.snapshot(pid), (old) => (old ? { ...old, project: { ...old.project, ...p } } : old));
}

setPendingSource((pid) => outbox.entries(pid));

outbox.register('project.update', {
  done: (e, r) => {
    const p = r as ProjectDto;
    // Noch offene spätere Einstellungen bleiben sichtbar (sonst springt z. B. die Werkzeugreihenfolge kurz zurück)
    queryClient.setQueryData<ProjectSnapshotDto>(keys.snapshot(e.projectId), (old) => (old && old.project.version <= p.version ? { ...old, project: withPendingProject(e.projectId!, p, e.opId) } : old));
  },
  fail: (e, err) => {
    // Einstellungen sind Absichten („setze …“): auf den neuen Stand setzen und erneut senden
    const current = err instanceof ApiError && err.code === 'version_conflict' ? (err.body.current as ProjectDto) : null;
    const n = ((e.meta ?? {}) as { rebases?: number }).rebases ?? 0;
    if (current && n < 3) return { opId: newOpId(), body: { ...(e.body as object), expectedVersion: current.version }, meta: { ...((e.meta ?? {}) as object), rebases: n + 1 } };
    void queryClient.invalidateQueries({ queryKey: keys.snapshot(e.projectId) });
    failed(err);
  },
});

type Patch = { name?: string; key?: string; phase?: ProjectPhase; startDate?: string | null; endDate?: string | null; tools?: ProjectTool[]; home?: ProjectHome; order?: ProjectHome[]; display?: ProjectDisplayDto | null; purpose?: string | null; template?: boolean; sharing?: ProjectDto['sharing']; health?: { kind: ProjectHealth; note: string | null } | null; archived?: boolean; overviewCards?: OverviewCard[] | null };

export function updateProject(project: ProjectDto, patch: Patch, optimistic: Partial<ProjectDto>) {
  applyProject(project.id, optimistic);
  const opId = newOpId();
  void outbox
    .enqueue({ opId, kind: 'project.update', projectId: project.id, method: 'PATCH', url: `/api/projects/${project.id}`, body: { opId, expectedVersion: project.version, patch }, meta: { optimistic } })
    .catch(() => {});
}

/** ADR 0122: archivieren (nur lesen für alle) bzw. wiederherstellen – nur die Leitung */
export function setArchived(project: ProjectDto, archived: boolean) {
  updateProject(project, { archived }, { archived });
}

export function setTool(project: ProjectDto, tool: ProjectTool, on: boolean, newHome?: ProjectHome) {
  const tools = on ? [...project.tools.filter((x) => x !== tool), tool] : project.tools.filter((x) => x !== tool);
  // W07: Wer die Startseite ausblendet, wählt vorher eine andere (newHome) – nie still auf die Aufgaben zurück
  const home = newHome ?? project.home;
  const order = on ? [...project.order.filter((x) => x !== tool), tool] : project.order.filter((x) => x !== tool);
  updateProject(project, newHome && newHome !== project.home ? { tools, home } : { tools }, { tools, home, order });
  if (!on) toast(t('tools.hidden', { name: t(`tools.${tool}`) }));
}

/** W03/W04: Darstellungsvorgabe für Liste oder Board (Projektleitung, gilt für alle ohne eigene Abweichung) */
export function setProjectDisplay(project: ProjectDto, view: 'list' | 'board', value: ListDisplay | BoardDisplay) {
  const display = { ...(project.display ?? DEFAULT_DISPLAY), [view]: value } as ProjectDisplayDto;
  updateProject(project, { display }, { display });
}

/** W07: Reihenfolge der Werkzeugleiste – getrennt von Zuschalten und Startseite, gilt für alle im Projekt */
export function setToolOrder(project: ProjectDto, order: ProjectHome[]) {
  updateProject(project, { order }, { order });
}

/** P25: Kürzel für Aufgaben-Schlüssel (das alte bleibt als Verweis gültig) */
export function setProjectKey(project: ProjectDto, key: string) {
  updateProject(project, { key }, { key });
}

/** P24: Phase des Projekts (Lobby-Gruppen, Zeitachse) */
export function setPhase(project: ProjectDto, phase: ProjectPhase) {
  updateProject(project, { phase }, { phase });
}

/** P24: geplanter Zeitraum – immer beide Tage zusammen, das Ende rückt nie vor den Beginn */
export function setSchedule(project: ProjectDto, startDate: string | null, endDate: string | null) {
  const end = startDate && endDate && endDate < startDate ? startDate : endDate;
  updateProject(project, { startDate, endDate: end }, { startDate, endDate: end });
}

export function setHome(project: ProjectDto, home: ProjectHome) {
  updateProject(project, { home }, { home });
}

/** Audit F09: Projekt umbenennen (Projekteinstellungen) */
export function setProjectName(project: ProjectDto, name: string) {
  updateProject(project, { name }, { name });
}

/** Audit F07: kurzer Zweck des Projekts (Übersicht) */
export function setPurpose(project: ProjectDto, purpose: string | null) {
  updateProject(project, { purpose }, { purpose });
}

/** P20: wer im Projekt Freigabelinks erzeugen darf („off“ legt alle Links still) */
export function setSharing(project: ProjectDto, sharing: ProjectDto['sharing']) {
  updateProject(project, { sharing }, { sharing });
}

/** P17: im Workspace als Vorlage anbieten (nur Projektleitung) */
export function setTemplate(project: ProjectDto, template: boolean) {
  updateProject(project, { template }, { template });
}

export function setHealth(project: ProjectDto, health: { kind: ProjectHealth; note: string | null } | null, me: string) {
  updateProject(project, { health }, { health: health ? { ...health, by: me, at: new Date().toISOString() } : null });
}

outbox.register('task.pin', {
  done: (e, r) => {
    upsertTask(e.projectId, r as TaskDto);
    void queryClient.invalidateQueries({ queryKey: keys.overview(e.projectId) });
  },
  fail: (_e, err) => failed(err),
});

/** Auf der Übersicht anheften bzw. lösen (für alle im Projekt) */
export function setTaskPinned(task: TaskDto, on: boolean) {
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'task.pin',
      projectId: task.projectId,
      method: 'PUT',
      url: `/api/tasks/${task.id}/pin`,
      body: { opId, on },
      overlay: { type: 'patch', taskId: task.id, fields: { onOverview: on } },
    })
    .catch(() => {});
  pinnedFeedback(task.projectId, task.title, on);
}

/** Ist die Übersicht im Projekt zugeschaltet? (für Menüs, die erst beim Öffnen gebaut werden) */
export function overviewEnabled(projectId: string): boolean {
  return !!queryClient.getQueryData<ProjectSnapshotDto>(keys.snapshot(projectId))?.project.tools.includes('overview');
}
